/**
 * V92-2-1: Jurisdiction-rule extraction service — the legislation→policy
 * drafter (the "A2 live" step, v9.2 Track 2).
 *
 * Clone of the v9.1 BYOP `control-spec-extraction-service`: LLM-assisted
 * extraction of a machine-readable rule (predicate AST against the
 * ADR-0024 frozen vocabulary + structured obligations) from legislation
 * text or a DPA event record. Produces a draft `JurisdictionRuleExtraction`
 * with confidence scoring (same gating thresholds as BYOP §8.3) that
 * pre-fills the review form and feeds `proposeRule()`.
 *
 * Pure function — no DB access, no platform deps, testable with a mock
 * IAIProvider. It NEVER writes `jurisdiction_rules`; that stays behind
 * the editor-gated `applyApprovedRuleState` (ADR-0039: agents propose,
 * deterministic code + human decide).
 *
 * V104-G-3 / ADR-0076 §6–§7: the schema asks for a `source_excerpt` on every
 * obligation, `predicate_excerpts` for the predicate, `missing_concepts`
 * (concepts the statute targets that the ADR-0024 vocabulary lacks) and
 * `rule_count_estimate`. Provider failures are no longer swallowed: when
 * every provider path fails, `extractJurisdictionRule` throws
 * `RuleExtractionProviderError` carrying each path's error, and the caller
 * escalates it as `provider_error`.
 */
import { defineTemplate, field } from '../egress/data-classes.js';
import { BOOLEAN_FIELDS, SCALAR_FIELDS, ARRAY_FIELDS, VOCABULARY, CANONICAL_ROLES, RULE_TIERS, canonicalScope, isRuleTier, } from './jurisdiction-applicability-service.js';
import { collectPredicateErrors } from './jurisdiction-rule-service.js';
import { RULE_DOMAINS, RULE_DOMAIN_LABELS, RISK_VECTORS, RISK_VECTOR_LABELS, } from '../vocabularies/index.js';
/**
 * V104-G-3: every provider path failed (threw, or answered with something
 * that is not the requested JSON object). ADR-0076 §7 `provider_error`.
 */
export class RuleExtractionProviderError extends Error {
    attempts;
    constructor(attempts) {
        super(attempts.length === 0
            ? 'no AI provider path can extract a rule (the provider has neither extract nor synthesize)'
            : `rule extraction failed on every provider path: ${attempts.map((a) => `${a.path}: ${a.error}`).join('; ')}`);
        this.attempts = attempts;
        this.name = 'RuleExtractionProviderError';
    }
}
// ---- Prompt constants (snapshot-stable) -----------------------------------
/**
 * Instruction prompt for LLM extraction. Kept as a const so tests can
 * snapshot the exact prompt — changes to this string ARE product changes.
 */
export const RULE_EXTRACTION_INSTRUCTIONS = [
    'You are extracting a machine-readable applicability rule from a piece of AI legislation.',
    '',
    'STATUTE: {title}',
    'JURISDICTION: {jurisdiction}',
    'FIELD HINTS (likely-relevant vocabulary fields): {field_hints}',
    'JURISDICTION TERMS (this jurisdiction\'s own words for who a duty falls on and for risk tiers):',
    '{jurisdiction_terms}',
    '',
    'LEGISLATION TEXT:',
    '{document_text}',
    '',
    'Extract as JSON:',
    '{',
    '  "predicate": <predicate AST — see grammar below>,',
    '  "obligations": [',
    '    {',
    '      "title": "Short imperative describing the duty",',
    '      "duty": "normalized_snake_case_duty_id",',
    '      "citation": "the section/article this obligation comes from",',
    '      "role_terms": ["term_key of a JURISDICTION TERMS role this duty falls on (optional)"],',
    '      "tier_term": "term_key of a JURISDICTION TERMS tier (optional)",',
    '      "tier": "{canonical_tiers} (optional; only when no tier term is listed)",',
    '      "scope": ["{canonical_roles}" (optional; only when no role term is listed)],',
    '      "effective_date": "YYYY-MM-DD (optional: only when the text gives this duty its own later start date, as in phased laws)",',
    '      "source_excerpt": "REQUIRED: an exact quote (at least 40 characters) from the LEGISLATION TEXT that imposes this duty"',
    '    }',
    '  ],',
    '  "predicate_excerpts": ["REQUIRED: exact quote(s), each at least 40 characters, from the LEGISLATION TEXT that define who or what the rule applies to"],',
    '  "missing_concepts": ["concepts the law targets that NO allowed field expresses (e.g. deepfakes_of_real_people); empty when the vocabulary covers it"],',
    '  "rule_count_estimate": <integer: how many separate applicability rules this law needs; 1 when one predicate covers it>,',
    '  "rationale": "One paragraph: which AI systems this rule applies to and why.",',
    '  "confidence": <0.0-1.0>,',
    '  "source_excerpts": ["Exact quote(s) from the text supporting the predicate and obligations."],',
    '  "rule_domains": ["1-3 values from RULE_DOMAINS below"],',
    '  "risk_vectors": ["1-4 values from RISK_VECTORS below"]',
    '}',
    '',
    'PREDICATE GRAMMAR (this is the ONLY allowed shape — no other fields):',
    '  { "and": [ <predicate>, ... ] }',
    '  { "or":  [ <predicate>, ... ] }',
    '  { "not": <predicate> }',
    '  { "eq": { "field": <field>, "value": <boolean|string|number> } }',
    '  { "in": { "field": <field>, "values": [ ... ] } }',
    '  { "contains": { "field": <array_field>, "value": <string> } }',
    '  { "non_empty": { "field": <array_field> } }',
    '',
    'ALLOWED boolean/scalar fields: {boolean_scalar_fields}',
    'ALLOWED array fields (for contains/non_empty): {array_fields}',
    '',
    'Rules:',
    '- Use ONLY fields from the allowed lists. Do NOT invent fields.',
    '- Prefer a single `eq` on the most specific boolean field the statute targets',
    '  (e.g. employment_decision, healthcare_decision, biometric_identification).',
    '- duty must be a lowercase_snake_case identifier reused across jurisdictions for the',
    '  same kind of obligation (e.g. bias_audit, consumer_notice, human_oversight, risk_management_system).',
    '- confidence: 1.0 = statute clearly targets an allowed field with an explicit obligation;',
    '  0.5 = inferred; 0.0 = the text does not map to the vocabulary.',
    '- If the text does not map to the vocabulary, return predicate null, empty obligations, confidence 0.0,',
    '  and name the concepts the vocabulary lacks in missing_concepts.',
    '- Every excerpt must be copied word for word from the LEGISLATION TEXT above. Never paraphrase,',
    '  summarize or invent a quote; an excerpt that is not in the text rejects the whole draft.',
    '- If the law needs more than one applicability rule (different triggers for different duties),',
    '  set rule_count_estimate to that number.',
    '- When the law names who a duty falls on, or a risk tier, use the term_key from JURISDICTION TERMS.',
    '  If the law defines such a word and it is not listed, give a new lowercase_snake_case term_key for it;',
    '  it is filed for an editor to declare. Never use "both": list each role.',
    '',
    'RULE_DOMAINS (pick 1-3 that describe the sector/domain this legislation regulates):',
    '{rule_domain_list}',
    '',
    'RISK_VECTORS (pick 1-4 risks this legislation is designed to mitigate):',
    '{risk_vector_list}',
].join('\n');
export const RULE_EXTRACTION_SCHEMA = {
    predicate: {},
    obligations: [{ title: 'string', duty: 'string', citation: 'string', role_terms: ['string (term_key, optional)'], tier_term: 'string (term_key, optional)', effective_date: 'YYYY-MM-DD (optional)', source_excerpt: 'string (exact quote, required)' }],
    predicate_excerpts: ['string (exact quote, at least one required)'],
    missing_concepts: ['string'],
    rule_count_estimate: 'integer (>= 1)',
    rationale: 'string',
    confidence: 'number (0.0-1.0)',
    source_excerpts: ['string'],
    rule_domains: ['string'],
    risk_vectors: ['string'],
};
// ---- Helpers -------------------------------------------------------------
/** Format a vocabulary enum + labels as "value — Label" lines for the prompt. */
function formatEnumList(values, labels) {
    return values.map((v) => `  ${v} — ${labels[v] ?? v}`).join('\n');
}
/**
 * ADR-0073: the rule-extraction prompt as reviewed templates. Platform scope:
 * legislation text and the vocabularies are public / GAC Pro-authored (reference);
 * the gateway throws if anything else reaches this call (R2).
 */
export const RULE_EXTRACTION_INPUT_TEMPLATE = defineTemplate('rules.extract.input', 'v2', RULE_EXTRACTION_INSTRUCTIONS, [
    'title', 'jurisdiction', 'field_hints', 'jurisdiction_terms', 'canonical_tiers', 'canonical_roles', 'document_text',
    'boolean_scalar_fields', 'array_fields', 'rule_domain_list', 'risk_vector_list',
]);
export const RULE_EXTRACTION_TEMPLATE = defineTemplate('rules.extract', 'v1', 'Extract a jurisdiction applicability rule as JSON.', []);
function promptSlots(input) {
    const leg = (v) => field('research.legislation_text', v);
    const vocab = (v) => field('rules.vocabulary', v);
    return {
        title: leg(input.title),
        jurisdiction: leg(input.jurisdiction || '(unknown)'),
        field_hints: vocab((input.field_hints ?? []).join(', ') || '(none)'),
        // V104-G-12 / ADR-0077 §5: the jurisdiction's declared role / tier words (editor-reviewed reference content).
        jurisdiction_terms: vocab(formatTerms(input.terms ?? [])),
        canonical_tiers: vocab(RULE_TIERS.join(' | ')),
        canonical_roles: vocab(CANONICAL_ROLES.join('" | "')),
        document_text: leg(truncateDocument(input.document_text)),
        boolean_scalar_fields: vocab([...BOOLEAN_FIELDS, ...SCALAR_FIELDS].join(', ')),
        array_fields: vocab(ARRAY_FIELDS.join(', ')),
        rule_domain_list: vocab(formatEnumList(RULE_DOMAINS, RULE_DOMAIN_LABELS)),
        risk_vector_list: vocab(formatEnumList(RISK_VECTORS, RISK_VECTOR_LABELS)),
    };
}
/** "role developer: Developer (C.R.S. 6-1-1701)" lines, roles first; "(none declared)" when empty. */
function formatTerms(terms) {
    if (terms.length === 0)
        return '  (none declared)';
    return [...terms]
        .sort((a, b) => (a.kind === b.kind ? a.term_key.localeCompare(b.term_key) : a.kind === 'role' ? -1 : 1))
        .map((t) => `  ${t.kind} ${t.term_key}: ${t.label} (${t.citation})`)
        .join('\n');
}
/**
 * V104-G-12 / ADR-0077 §5, deterministic (ADR-0039: the model proposes, code
 * decides): map each obligation's term keys to canonical values from the
 * jurisdiction's ACTIVE terms. A known role term sets `scope` to its canonical
 * roles and a known tier term sets `tier`; a key the jurisdiction has not
 * declared is recorded in `missing_terms` (a vocabulary gap). Pure.
 */
export function resolveExtractionTerms(extraction, terms) {
    const active = terms.filter((t) => t.status === 'active');
    const find = (kind, key) => active.find((t) => t.kind === kind && t.term_key === key);
    const missing = [];
    const note = (kind, term_key) => {
        if (!missing.some((m) => m.kind === kind && m.term_key === term_key))
            missing.push({ kind, term_key });
    };
    const obligations = extraction.obligations.map((o) => {
        const next = { ...o };
        if (o.role_terms && o.role_terms.length > 0) {
            const canon = [];
            let complete = true;
            for (const key of o.role_terms) {
                const t = find('role', key);
                if (!t) {
                    note('role', key);
                    complete = false;
                }
                else if (!canon.includes(t.canonical))
                    canon.push(t.canonical);
            }
            if (complete)
                next.scope = canon;
        }
        if (o.tier_term) {
            const t = find('tier', o.tier_term);
            if (!t)
                note('tier', o.tier_term);
            else
                next.tier = t.canonical;
        }
        return next;
    });
    return { ...extraction, obligations, missing_terms: missing };
}
function truncateDocument(text, maxChars = 50_000) {
    if (text.length <= maxChars)
        return text;
    return text.slice(0, maxChars) + '\n\n[... document truncated ...]';
}
/** A provider answer is usable when it is a JSON object carrying at least one schema field. */
function isUsableResponse(raw) {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw))
        return false;
    return ['predicate', 'obligations', 'missing_concepts', 'rule_count_estimate'].some((k) => k in raw);
}
function stringList(raw) {
    if (!Array.isArray(raw))
        return [];
    const out = [];
    for (const v of raw)
        if (typeof v === 'string' && v.trim())
            out.push(v.trim());
    return out;
}
/** Field names from `unknown predicate field '<name>'` validation errors. */
function unknownFieldsOf(errors) {
    const out = [];
    for (const e of errors) {
        const m = /unknown predicate field '([^']+)'/.exec(e);
        if (m && !out.includes(m[1]))
            out.push(m[1]);
    }
    return out;
}
const RULE_DOMAINS_SET = new Set(RULE_DOMAINS);
const RISK_VECTORS_SET = new Set(RISK_VECTORS);
/** Filter an LLM-returned array to only values present in the allowed set. */
function normalizeEnumArray(raw, allowed) {
    if (!Array.isArray(raw))
        return [];
    const out = [];
    for (const v of raw) {
        if (typeof v === 'string' && allowed.has(v) && !out.includes(v)) {
            out.push(v);
        }
    }
    return out;
}
function slugifyDuty(s) {
    return s.trim().toLowerCase().replace(/[\s-]+/g, '_').replace(/[^a-z0-9_]/g, '');
}
const TERM_KEY_RE = /^[a-z][a-z0-9_]{1,63}$/;
function normalizeObligations(raw, statuteTitle) {
    const excerpts = [];
    if (!Array.isArray(raw))
        return { obligations: [], excerpts };
    const out = [];
    raw.forEach((o, i) => {
        if (!o || typeof o !== 'object')
            return;
        const rec = o;
        const title = typeof rec.title === 'string' ? rec.title.trim() : '';
        const dutyRaw = typeof rec.duty === 'string' ? rec.duty : '';
        if (!title || !dutyRaw)
            return;
        const duty = slugifyDuty(dutyRaw);
        if (!duty)
            return;
        const ob = {
            obligation_id: `${slugifyDuty(statuteTitle).slice(0, 32) || 'rule'}-${duty}-${i}`,
            title,
            duty,
            deadline_rule: { kind: 'none' },
            citation: typeof rec.citation === 'string' && rec.citation.trim() ? rec.citation.trim() : '(see source)',
        };
        if (isRuleTier(rec.tier)) {
            ob.tier = rec.tier;
        }
        // ADR-0077 §5: canonical roles only ("both" → provider + deployer; anything else dropped).
        const scope = canonicalScope(rec.scope);
        if (scope)
            ob.scope = scope;
        // ADR-0077 §3: the jurisdiction's own words, resolved to canonical values by resolveExtractionTerms.
        const roleTerms = stringList(rec.role_terms).map(slugifyDuty).filter((k) => TERM_KEY_RE.test(k));
        if (roleTerms.length > 0)
            ob.role_terms = [...new Set(roleTerms)];
        const tierTerm = typeof rec.tier_term === 'string' ? slugifyDuty(rec.tier_term) : '';
        if (TERM_KEY_RE.test(tierTerm))
            ob.tier_term = tierTerm;
        // V104-G-11: optional per-duty start date; anything that is not a plain ISO date is dropped.
        if (typeof rec.effective_date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(rec.effective_date.trim())) {
            ob.effective_date = rec.effective_date.trim();
        }
        out.push(ob);
        excerpts.push(typeof rec.source_excerpt === 'string' && rec.source_excerpt.trim() ? rec.source_excerpt.trim() : null);
    });
    return { obligations: out, excerpts };
}
/**
 * Parse and validate the LLM response. Gracefully handles malformed
 * responses — returns low confidence with null predicate rather than
 * throwing. The predicate is soft-validated against the ADR-0024
 * vocabulary; an invalid predicate is kept for the editor to see but
 * flagged `predicate_valid: false` and its confidence is capped.
 */
function normalizeExtraction(raw, statuteTitle) {
    const empty = {
        predicate: null,
        obligations: [],
        rationale: '',
        confidence: 0,
        source_excerpts: [],
        predicate_valid: false,
        rule_domains: [],
        risk_vectors: [],
        predicate_excerpts: [],
        obligation_excerpts: [],
        raw_obligation_count: 0,
        missing_concepts: [],
        rule_count_estimate: 1,
        unknown_fields: [],
        predicate_errors: [],
    };
    if (!raw || typeof raw !== 'object')
        return empty;
    const data = raw;
    const { obligations, excerpts: obligationExcerpts } = normalizeObligations(data.obligations, statuteTitle);
    const rationale = typeof data.rationale === 'string' ? data.rationale.trim() : '';
    let confidence = 0;
    if (typeof data.confidence === 'number' && isFinite(data.confidence)) {
        confidence = Math.max(0, Math.min(1, data.confidence));
    }
    const excerpts = [];
    if (Array.isArray(data.source_excerpts)) {
        for (const e of data.source_excerpts) {
            if (typeof e === 'string' && e.trim())
                excerpts.push(e.trim());
        }
    }
    // Soft-validate the predicate against the frozen vocabulary.
    let predicate = null;
    let predicateValid = false;
    let predicateErrors = [];
    if (data.predicate && typeof data.predicate === 'object') {
        predicate = data.predicate;
        // V104-G-3: keep the validation errors instead of discarding them; an
        // unknown field is a vocabulary gap (ADR-0076 §7).
        predicateErrors = collectPredicateErrors(predicate);
        predicateValid = predicateErrors.length === 0;
        if (!predicateValid) {
            // An invalid predicate cannot ship as-is; cap confidence so the
            // gate treats it as "suggestions only".
            confidence = Math.min(confidence, 0.4);
        }
    }
    if (!predicate || obligations.length === 0) {
        confidence = Math.min(confidence, 0.4);
    }
    // V94-2-1: normalize rule_domains and risk_vectors against the frozen vocabularies.
    const ruleDomains = normalizeEnumArray(data.rule_domains, RULE_DOMAINS_SET);
    const riskVectors = normalizeEnumArray(data.risk_vectors, RISK_VECTORS_SET);
    const ruleCount = typeof data.rule_count_estimate === 'number' && Number.isFinite(data.rule_count_estimate)
        ? Math.max(1, Math.floor(data.rule_count_estimate))
        : 1;
    return {
        predicate,
        obligations,
        rationale,
        confidence,
        source_excerpts: excerpts,
        predicate_valid: predicateValid,
        rule_domains: ruleDomains,
        risk_vectors: riskVectors,
        predicate_excerpts: stringList(data.predicate_excerpts),
        obligation_excerpts: obligationExcerpts,
        raw_obligation_count: Array.isArray(data.obligations) ? data.obligations.length : 0,
        missing_concepts: stringList(data.missing_concepts),
        rule_count_estimate: ruleCount,
        unknown_fields: unknownFieldsOf(predicateErrors),
        predicate_errors: predicateErrors,
    };
}
// ---- Main extraction function --------------------------------------------
/**
 * Extract a jurisdiction rule from legislation text using an LLM provider.
 * Uses `extract` if available, falls back to `synthesize`. Returns a draft
 * with confidence scoring; the caller reviews and feeds `proposeRule()`.
 *
 * V104-G-3: a path that throws, or answers with something that is not the
 * requested JSON object, is recorded and the next path is tried. When no
 * path yields a usable answer this throws `RuleExtractionProviderError` with
 * every path's error; nothing is swallowed.
 */
export async function extractJurisdictionRule(input, llm) {
    const failures = [];
    try {
        const result = await llm.run({
            task: 'rules.extract', template: RULE_EXTRACTION_TEMPLATE, inputTemplate: RULE_EXTRACTION_INPUT_TEMPLATE,
            slots: promptSlots(input), schemaHint: RULE_EXTRACTION_SCHEMA,
        });
        if (result.status !== 'ok') {
            // A refusal (no platform provider can extract here) is a failed path too: nothing is swallowed (V104-G-3).
            failures.push({ path: 'extract', error: `the AI gateway refused the call (${result.reason})` });
        }
        else if (isUsableResponse(result.output)) {
            const extraction = normalizeExtraction(result.output, input.title);
            if (extraction.confidence === 0 && result.confidence > 0 && extraction.predicate_valid) {
                extraction.confidence = Math.min(result.confidence, 0.3);
            }
            return extraction;
        }
        else {
            failures.push({ path: 'extract', error: 'the response was not the requested JSON object' });
        }
    }
    catch (err) {
        // R2 (tenant content on a platform call) is a programming error: never swallowed.
        if (err instanceof Error && err.name === 'PlatformScopeViolation')
            throw err;
        failures.push({ path: 'extract', error: errorMessage(err) });
    }
    throw new RuleExtractionProviderError(failures);
}
function errorMessage(err) {
    return (err instanceof Error ? err.message : String(err)).slice(0, 300) || 'unknown error';
}
/**
 * Map a confidence score to a review-form gate. ≥0.8 full prefill,
 * 0.5–0.8 prefill with per-field warnings, <0.5 suggestions only. An
 * invalid predicate never rises above suggestions_only.
 */
export function gateForExtraction(extraction) {
    if (!extraction.predicate_valid)
        return 'suggestions_only';
    if (extraction.confidence >= 0.8)
        return 'full_prefill';
    if (extraction.confidence >= 0.5)
        return 'prefill_with_warnings';
    return 'suggestions_only';
}
/**
 * Walk a predicate and collect a classification that SATISFIES it, plus
 * one that VIOLATES it. Best-effort — handles the common shapes the
 * extractor emits (eq / in / contains / non_empty / and / or / not). The
 * generated fixtures are validated by `validateTestFixtures` +
 * `evaluatePredicate` at propose time; anything that doesn't hold is
 * surfaced to the editor rather than silently shipped.
 */
export function generateRuleFixtures(extraction) {
    const p = extraction.predicate;
    if (!p)
        return [];
    const positive = {};
    const negative = {};
    collectSatisfying(p, positive, negative);
    return [
        { label: 'positive — matches the rule', classification: positive, expected: true },
        { label: 'negative — does not match the rule', classification: negative, expected: false },
    ];
}
const isBoolField = (f) => BOOLEAN_FIELDS.includes(f);
const isArrayField = (f) => ARRAY_FIELDS.includes(f);
/**
 * Populate `pos` so it satisfies the predicate and `neg` so it fails.
 * Only the FIRST leaf drives the negative (flipping one condition is
 * enough to violate an `and`/`eq`); this keeps fixtures simple and is why
 * the editor confirms them.
 */
function collectSatisfying(pred, pos, neg, negDriven = { done: false }) {
    const rec = pred;
    if ('and' in rec && Array.isArray(rec.and)) {
        for (const child of rec.and)
            collectSatisfying(child, pos, neg, negDriven);
        return;
    }
    if ('or' in rec && Array.isArray(rec.or)) {
        // Satisfy the first branch for the positive case.
        if (rec.or.length > 0)
            collectSatisfying(rec.or[0], pos, neg, negDriven);
        return;
    }
    if ('not' in rec && rec.not) {
        // A `not` is satisfied by leaving the inner field unset in pos and
        // setting it in neg — approximate; editor confirms.
        const inner = rec.not;
        if ('eq' in inner) {
            const { field, value } = inner.eq;
            if (isBoolField(field))
                neg[field] = value;
        }
        return;
    }
    if ('eq' in rec) {
        const { field, value } = rec.eq;
        pos[field] = value;
        if (!negDriven.done) {
            neg[field] = isBoolField(field) ? !value : `__not_${String(value)}`;
            negDriven.done = true;
        }
        return;
    }
    if ('in' in rec) {
        const { field, values } = rec.in;
        if (Array.isArray(values) && values.length > 0) {
            pos[field] = values[0];
            if (!negDriven.done) {
                neg[field] = isBoolField(field) ? !values[0] : `__not_in`;
                negDriven.done = true;
            }
        }
        return;
    }
    if ('contains' in rec) {
        const { field, value } = rec.contains;
        if (isArrayField(field)) {
            pos[field] = [value];
            if (!negDriven.done) {
                neg[field] = [];
                negDriven.done = true;
            }
        }
        return;
    }
    if ('non_empty' in rec) {
        const { field } = rec.non_empty;
        if (isArrayField(field)) {
            pos[field] = ['x'];
            if (!negDriven.done) {
                neg[field] = [];
                negDriven.done = true;
            }
        }
        return;
    }
}
/** The vocabulary, exported for callers that want to show valid fields. */
export { VOCABULARY };
//# sourceMappingURL=jurisdiction-rule-extraction-service.js.map